<template>
    <div class="panel-map">
        <div id="map" class="panel-map__map"></div>
        <div class="panel-map__button-container">
            <button class="btn btn-primary" @click="exportAllKML" style="margin-right: 10px;">
                <i class="fa fa-download" />
                <span>Exportar KML</span>
            </button>
            <button class="btn btn-primary" @click="closePopups">
                <img src="/img/icons/svg/codigo-limpo.svg" alt="limpar">
                <span>Limpar todos os detalhes</span>
            </button>
        </div>
        <div class="layer-switch">
            <select v-model="currentMapLayer">
                <option value="mapbox">Mapbox</option>
                <option value="google">Google</option>
            </select>
        </div>
    </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { drawPolygons } from "@/utils/drawPolygons";
import { mapGetters, mapActions } from "vuex";

export default {
    name: "PanelMap",
    data() {
        return {
            map: null,
            polygons: [],
            payload: {
                propertyId: "",
                cropId: ""
            },
            mapCenter: { latitude: -11.8831661, longitude: -60.1766577 },
            screen: 0,
            currentMapLayer: 'google',
            mapboxLayer: null,
            googleLayer: null,
            mapUrls: {
                google: {
                    url: "http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
                    attribution: "Tiles © Google",
                    subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
                },
            },
        };
    },
    computed: {
        ...mapGetters("property", ["getPropertySelected", "getCropSelected"]),
        ...mapGetters("fields", ["allFields"]),
    },
    async mounted() {
        this.initializeMap();
        this.updateMap();
        this.updatePayload();
        await this.getFieldsByPropertyByCrop(this.payload);
        this.updateMap();
    },
    watch: {
        async getPropertySelected() {
            this.updatePayload();
            await this.getFieldsByPropertyByCrop(this.payload);
            this.updateMap();
        },
        async getCropSelected() {
            this.updatePayload();
            await this.getFieldsByPropertyByCrop(this.payload);
            this.updateMap();
        },
        currentMapLayer(newLayer) {
            if (this.map) {
                this.updateMapLayer(newLayer);
            }
        },
    },
    methods: {
        ...mapActions("fields", ["getFieldsByPropertyByCrop"]),
        updatePayload() {
            this.payload = {
                propertyId: this.getPropertySelected._id ? this.getPropertySelected._id : '',
                cropId: this.getCropSelected._id ? this.getCropSelected._id : ''
            }
        },
        initializeMap() {
            this.map = L.map('map', {
                center: [this.mapCenter.latitude, this.mapCenter.longitude],
                zoom: 12,
                zoomControl: false
            });

            const mapboxAccessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;

            this.mapboxLayer = L.tileLayer(
                `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=${mapboxAccessToken}`,
                {
                    attribution: '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                    tileSize: 512,
                    zoomOffset: -1,
                    maxZoom: 19,
                }
            );

            this.googleLayer = L.tileLayer(this.mapUrls.google.url, {
                attribution: this.mapUrls.google.attribution,
                subdomains: this.mapUrls.google.subdomains,
                maxZoom: 19
            });

            this.updateMapLayer(this.currentMapLayer);

            L.control.zoom({ position: 'topleft' }).addTo(this.map);
        },
        updateMapLayer(newLayer) {
            if (!this.mapboxLayer || !this.googleLayer) {
                this.initializeMap();
                return;
            }

            if (this.map) {
                if (this.map.hasLayer(this.mapboxLayer)) {
                    this.map.removeLayer(this.mapboxLayer);
                }
                if (this.map.hasLayer(this.googleLayer)) {
                    this.map.removeLayer(this.googleLayer);
                }

                if (newLayer === "mapbox") {
                    this.map.addLayer(this.mapboxLayer);
                } else if (newLayer === "google") {
                    this.map.addLayer(this.googleLayer);
                }
            }
        },

        updateMap() {
            const fields = this.allFields || [];
            const crop = this.getCropSelected;
            this.polygons = drawPolygons(fields, crop);

            this.map.eachLayer(layer => {
                if (layer instanceof L.Polygon) {
                    this.map.removeLayer(layer);
                }
            });

            const polygonLayers = [];
            this.polygons.forEach(polygon => {
                const layer = L.polygon(polygon.latlngs, {
                    color: polygon.color,
                    fillColor: polygon.fillColor
                }).addTo(this.map);
                layer.bindPopup(polygon.text);
                polygonLayers.push(layer);
            });

            if (polygonLayers.length > 0) {
                const featureGroup = L.featureGroup(polygonLayers);
                this.map.fitBounds(featureGroup.getBounds().pad(0.1));
            } else {
                const latitude = (this.getPropertySelected && this.getPropertySelected.coordinates && this.getPropertySelected.coordinates.latitude) || this.mapCenter.latitude;
                const longitude = (this.getPropertySelected && this.getPropertySelected.coordinates && this.getPropertySelected.coordinates.longitude) || this.mapCenter.longitude;
                this.map.setView([latitude, longitude], 13);
            }
        },
        closePopups() {
            this.map.eachLayer(layer => {
                if (layer instanceof L.Popup) {
                    layer.closePopup();
                }
            });
        },
        extractNameFromHtml(htmlString) {
            const regex = /Nome:\s*<\/div>\s*<div[^>]*>\s*([^<\s]+)\s*<\/div>/;
            const match = htmlString.match(regex);
            return match && match[1] ? match[1].trim() : 'Polígono';
        },
        exportAllKML() {
            if (!this.polygons || this.polygons.length === 0) {
                this.$vToastify.error("Não há polígonos para exportar.");
                return;
            }

            const placemarks = this.polygons.map(polygon => {
                const coordinatesString = polygon.latlngs
                    .map(latlng => `${latlng[1]},${latlng[0]},0`)
                    .join(' ');

                const polygonName = this.extractNameFromHtml(polygon.text);
                console.log(polygonName)
                return `
                <Placemark>
                    <name>${polygonName}</name>
                    <Polygon>
                        <outerBoundaryIs>
                        <LinearRing>
                            <coordinates>${coordinatesString}</coordinates>
                        </LinearRing>
                        </outerBoundaryIs>
                    </Polygon>
                </Placemark>`;
            }).join('');

            const propertyName = this.getPropertySelected.name || 'propriedade';
            const kmlContent = `<?xml version="1.0" encoding="UTF-8"?>
            <kml xmlns="http://www.opengis.net/kml/2.2">
                <Document>
                    <name>${propertyName}</name>
                    ${placemarks}
                </Document>
            </kml>`;

            const blob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `${propertyName.replace(/\s+/g, '_').toLowerCase()}_talhoes.kml`;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }
};
</script>

<style scoped lang="sass">
.panel-map
    height: 100%
    position: relative

    &__map
        height: 100%
        width: 100%

    &__button-container
        display: flex
        justify-content: center
        align-items: center
        flex-direction: row
        flex-wrap: nowrap
        position: absolute
        z-index: 400
        width: 100%
        height: 50px
        bottom: 24px

        button
          display: flex
          align-items: center
          gap: 8px

        img
            width: 25px
            height: 25px

.leaflet-verticalcenter
    position: absolute
    z-index: 1000
    pointer-events: none
    top: 50%
    transform: translateY(-50%)
    padding-top: 10px

.leaflet-verticalcenter .leaflet-control
    margin-bottom: 10px

.leaflet-control-zoom-in,
.leaflet-control-zoom-out
    float: right
    font: bold 18px "Lucida Console", Monaco, monospace
    text-indent: 1px

.leaflet-control-zoom-out
    font-size: 20px

.leaflet-touch .leaflet-control-zoom-in
    font-size: 22px

.leaflet-touch .leaflet-control-zoom-out
    font-size: 24px

.leaflet-top
    bottom: 0

.leaflet-top .leaflet-control-zoom
    top: 50%
    transform: translateY(-50%)

.layer-switch
    position: absolute
    bottom: 10px
    left: 25px
    z-index: 1000
    background-color: white
    padding: 5px
    border-radius: 5px
</style>